import {
  DeleteConfirmation,
  Divider, ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import {
  Link, useNavigate, useParams, useLocation
} from "react-router-dom";
import {
  Breadcrumb,
  Button, Card, Col, Container, Form, FormGroup, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useCallback, useEffect, useState } from "react";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";

import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

import "./EventsAndIncidents.scss";
import { convertToDateFromLocalDate } from "helpers/DateUtils";
import IEditNotableEventIncidentRequestModel from "interfaces/Request/IEditNotableEventIncidentRequestModel";
import notableEventIncidentSchema from "modules/schemas/notableEventIncidentSchema";

const EditEventAndIncident = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { hwrcSiteId, diaryDate, eventIncidentId } = useParams();
  const editDetails = { ...location.state as IEditNotableEventIncidentRequestModel };
  const returnUrl = `${RoutePathsEnum.SiteDiaryLandingPage}${hwrcSiteId && diaryDate ? `/${hwrcSiteId}/${diaryDate}` : ""}`;
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState<boolean>(true);
  const [deleteButtonPressed, setDeleteButtonPressed] = useState<boolean>(false);

  const [timestampRef, setTimestampRef] = useState<TimePickerComponent | undefined>();

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues
  } = useForm<IEditNotableEventIncidentRequestModel>({
    resolver: yupResolver(notableEventIncidentSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    isSending: updateEventIncidentIsSending,
    errors: updateEventIncidentErrors,
    sendRequest: updateEventIncidentSendRequest,
    statusCode: updateEventIncidentStatusCode,
    resetState: updateEventIncidentResetState
  } = useAmplifyREST();

  const {
    isSending: deleteEventIncidentIsSending,
    errors: deleteEventIncidentErrors,
    sendRequest: deleteEventIncidentSendRequest,
    statusCode: deleteEventIncidentStatusCode
  } = useAmplifyREST();

  const isSending = updateEventIncidentIsSending || deleteEventIncidentIsSending;
  const apiErrors = updateEventIncidentErrors || deleteEventIncidentErrors;

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const handleUpdateButtonClick = (request : IEditNotableEventIncidentRequestModel) => {
    const time = convertToDateFromLocalDate(diaryDate!);
    time.setHours(timestampRef!.value.getHours());
    time.setMinutes(timestampRef!.value.getMinutes());

    request.timestamp = time.toISOString();
    updateEventIncidentResetState();
    updateEventIncidentSendRequest(encodeURI(`hwrcsitediary/eventincident/${eventIncidentId}`), "PUT", request);
  };

  useEffect(() => {
    if (updateEventIncidentStatusCode === 200) {
      navigate(returnUrl);
    }
  }, [updateEventIncidentStatusCode]);

  const handleDeleteButtonClick = () => {
    setDeleteButtonPressed(false);
    deleteEventIncidentSendRequest(encodeURI(`hwrcsitediary/eventincident/${eventIncidentId}`), "DELETE");
  };

  useEffect(() => {
    if (deleteEventIncidentStatusCode === 200) {
      navigate(returnUrl);
    }
  }, [deleteEventIncidentStatusCode]);

  useEffect(() => {
    if (deleteEventIncidentStatusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [deleteEventIncidentStatusCode]);

  useEffect(() => {
    if (updateEventIncidentStatusCode === 401) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [updateEventIncidentStatusCode]);

  const canFormBeSubmitted = () => {
    const mandatoryFieldsEntered = (getValues("notes").length > 0 && timestampRef?.value != null);

    let timestampChanged = false;
    let timeCalc = convertToDateFromLocalDate(diaryDate!);

    if (timestampRef?.value) {
      timeCalc = convertToDateFromLocalDate(diaryDate!);
      timeCalc.setHours(timestampRef!.value.getHours());
      timeCalc.setMinutes(timestampRef!.value.getMinutes());
      timestampChanged = timeCalc.getTime() !== new Date(editDetails.timestamp).getTime();
    }

    const valueHasChanged = getValues("notes") !== editDetails.notes
    || timestampChanged;

    setUpdateButtonDisabled(!(mandatoryFieldsEntered && valueHasChanged));
  };

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h1>
            Edit Notable Events and Incidents
            <Spinner
              animation="grow"
              visible={isSending}
              spinnerText="Loading"
              variant="info"
            />
          </h1>
        </Card.Header>
        <Card.Body className="no-padding">
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleUpdateButtonClick)}
          >
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item
                    data-testid="breadcrumb-diary"
                    linkAs={Link}
                    linkProps={{ to: returnUrl }}
                    onClick={() => navigate(returnUrl)}
                  >
                    Diary
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active data-testid="breadcrumb-edit-event-incident">
                    Edit Notable Events and Incidents
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end">
                <IconButton
                  iconType={IconButtonType.Delete}
                  variant="danger"
                  className="right-margin-small"
                  aria-disabled={deleteEventIncidentIsSending}
                  aria-label="Delete event or incident"
                  type="button"
                  spin={deleteEventIncidentIsSending}
                  onClick={() => setDeleteButtonPressed(true)}
                  data-testid="btnDelete"
                >
                  Delete
                </IconButton>
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={updateButtonDisabled || updateEventIncidentIsSending}
                  aria-disabled={updateButtonDisabled || updateEventIncidentIsSending}
                  type="submit"
                  spin={updateEventIncidentIsSending}
                  data-testid="btnUpdate"
                  aria-label="Update event or incident"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(returnUrl)}
                  aria-label="Cancel update and return to site diary"
                  data-testid="cancel"
                >
                  Cancel
                </Button>
              </Col>
            </Row>

            <Divider />
            <section className="event-incident pt-2">
              <Container fluid>
                <Row>
                  <div className="control-pane">
                    <div className="control-section" />

                    <Row className="mt-3">
                      <div className="w-auto">
                        <FormGroup as={Row} className="mb-6 d-flex time" controlId="timestamp">
                          <Form.Label id="timestampLabel" className="d-inline-flex horiz-form-label w-auto mb-0 me-3">
                            Time:
                          </Form.Label>
                          <div className="ml-1 mt-1">
                            <TimePickerComponent
                              ref={(t: TimePickerComponent) => { setTimestampRef(t); }}
                              data-testid="timestamp"
                              id="timestamp"
                              type="text"
                              htmlAttributes={{ "aria-labelledby": "timestampLabel", "aria-required": "true" }}
                              format="HH:mm"
                              step={15}
                              value={new Date(editDetails.timestamp)}
                              change={() => { canFormBeSubmitted(); }}
                              showClearButton={false}
                            />
                          </div>
                        </FormGroup>
                      </div>
                    </Row>
                    <Row className="mt-3 mb-3">
                      <FormGroup as={Row} controlId="notes">
                        <Form.Label id="notesLabel" className="horiz-form-label">
                          Details:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="true"
                            aria-labelledby="notesLabel"
                            aria-invalid={ariaInvalidCheck(
                              "notes",
                              errors,
                              null
                            )}
                            maxLength={50}
                            minLength={1}
                            onKeyUp={() => canFormBeSubmitted()}
                            {...register("notes")}
                            defaultValue={editDetails.notes}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </FormGroup>
                    </Row>
                  </div>
                </Row>
              </Container>

            </section>

          </Form>
        </Card.Body>
      </Card>
      <DeleteConfirmation
        title="Delete Event Or Incident"
        onDelete={() => { handleDeleteButtonClick(); }}
        onHide={() => { setDeleteButtonPressed(false); }}
        isDeleting={deleteEventIncidentIsSending}
        show={deleteButtonPressed}

      >
        <p>Are you sure you want to delete this Event or Incident?</p>
      </DeleteConfirmation>
    </Page>
  );
};

export default EditEventAndIncident;
